import { CHANNEL_SWITCH_PROFILE, SCROLL_PROFILE } from "./debug-flags";

// ─── Long-task observer ────────────────────────────────────────────────
// Cross-reference signal for `evDelay` spikes. The browser's `longtask`
// PerformanceEntry fires for any main-thread task >50ms. When the user
// is navigating channels and a long task ends just before a [cs] line
// with a high evDelay, we've identified the cause window — main thread
// was blocked. Subsequent diagnosis (Chrome DevTools Performance
// recording, more targeted instrumentation) can zero in on which code
// is responsible. `name` on the entry distinguishes own-document work
// (`self`) from same-origin iframes etc.; for our case it's almost
// always `self`.
//
// Output: `[lt] dur=87ms start=12345 name=self` — chronologically
// interleaved with [cs] lines so you can read the surrounding
// switches.
let longTaskObserverStarted = false;
function ensureLongTaskObserver(): void {
  if (longTaskObserverStarted) return;
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // longtask spec is >50ms; some browsers fire for shorter
        // anyway — gate so we don't drown in noise.
        if (entry.duration < 50) continue;

        console.log(
          `[lt] dur=${entry.duration.toFixed(0)}ms ` +
            `start=${entry.startTime.toFixed(0)} ` +
            `end=${(entry.startTime + entry.duration).toFixed(0)} ` +
            `name=${entry.name}`,
        );
      }
    });
    obs.observe({ entryTypes: ["longtask"] });
    longTaskObserverStarted = true;
  } catch {
    // `longtask` not supported (older browsers, some test envs) —
    // silently skip. The rest of the profile still works.
  }
}
if (CHANNEL_SWITCH_PROFILE || SCROLL_PROFILE) {
  ensureLongTaskObserver();
}

/**
 * Channel-switch latency profile. Enable with
 * `localStorage.setItem('ano-cs-profile', '1')` then reload.
 *
 * Captures four marks per channel switch and emits ONE line when the
 * channel is settled (scroll restored / pin-to-bottom done):
 *
 *   click   — user-initiated channel-switch entry (App.openChannelWithContext)
 *   emit    — first message-list emit for the new channel (useChannelMessageList)
 *   paint   — post-render rAF after MessageArea became active
 *   settled — scroll-target resolved (anchor restore) OR pin-to-bottom done
 *
 * Output shape:
 *   [cs] hiring click→emit=22ms emit→paint=48ms paint→settled=-14ms total=55ms msgs=160 q=2 since=85ms evDelay=14ms src=kbd click=12345
 *   [lt] dur=87ms start=12200 end=12287 name=self    (main-thread blocking task)
 *
 * Extra fields:
 *   q=N        — other channel-switches still IN FLIGHT (clicked but
 *                not yet settled) when THIS one's click fired. High
 *                values during rapid Alt+↑/↓ navigation mean React
 *                is committing multiple switches back-to-back and
 *                each one waits its turn.
 *   since=Nms  — ms between this click and the previous one (any
 *                channel, any source). Useful for spotting the user's
 *                typing rhythm vs the burst-debounce window.
 *   evDelay=Nms — ms between the physical keydown's `timeStamp` and
 *                the moment our handler ran. High values mean the
 *                browser sat on the keystroke because the main
 *                thread was busy with prior work (e.g. a previous
 *                switch's React commit). Only present when the
 *                click came from a keyboard event.
 *   src=…      — `kbd` for Alt+arrow, `click` for mouse-on-sidebar,
 *                `other` for programmatic / notification / etc.
 *
 * If a stage is missing, the corresponding span is omitted. A second
 * `click` for the same channel replaces the in-flight entry.
 *
 * ─── Ordering note ────────────────────────────────────────────────
 * `settled` is typically called from React commit-phase callbacks
 * (MessageScrollContainer.componentDidUpdate, useEffect bodies). The
 * `paint` mark fires from a post-commit rAF in useRenderDiagnostics.
 * If `settled` emitted synchronously it would clear the entry BEFORE
 * the paint rAF could record. To capture both, `settled` records the
 * timestamp synchronously but defers the log emission through a
 * double-rAF chain: one frame to let post-commit paint marks land,
 * one frame to flush past the next paint boundary.
 * ──────────────────────────────────────────────────────────────────
 *
 * Reads CHANNEL_SWITCH_PROFILE once at module load — the per-call cost
 * when off is a single boolean test.
 */

type Stage = "click" | "emit" | "paint" | "settled";

interface Entry {
  click: number;
  emit?: number;
  paint?: number;
  settled?: number;
  msgs?: number;
  /** Number of OTHER switches still in-flight when this click fired. */
  qAtClick: number;
  /** ms between this click and the previous one (-1 if first ever). */
  sinceLastClick: number;
  /** ms between the physical keydown and this click handler running
   *  (-1 if the click came from a non-keyboard source like a sidebar
   *  mouse click). */
  evDelayMs: number;
  /** Origin of the click — `kbd` (Alt+arrow), `click`, or `other`. */
  source: ClickSource;
}

export type ClickSource = "kbd" | "click" | "other";

const entries = new Map<string, Entry>();

/** Module counter: switches that have clicked but not yet settled. */
let inFlightCount = 0;
/** Performance.now() of the most recent click (any source). */
let lastClickAt = -1;

/**
 * Set by external callers (e.g. `useCommandShortcuts` for the
 * keyboard path) RIGHT BEFORE the action that will call csMark("click").
 * Cleared by the next csMark("click") call so it can't leak into the
 * next, unrelated, click.
 */
let pendingClickHint: { evTimeStamp: number; source: ClickSource } | null =
  null;

/**
 * Record that the next csMark("click") originated from a keyboard event.
 * Pass the DOM event's `timeStamp` so we can measure handler-queue delay
 * (how long the OS/browser waited before running our keydown listener,
 * which spikes when the main thread is busy committing a previous
 * channel switch).
 */
export function csNoteClickFromEvent(
  source: ClickSource,
  evTimeStamp: number,
): void {
  // Not gated by CHANNEL_SWITCH_PROFILE — the hint is also consumed
  // by `App.openChannelWithContext` via `csPeekClickSource()` to
  // decide whether the resulting state-update bag should run as a
  // `startTransition` (kbd burst → interruptible, latest-wins) or
  // synchronously (one-shot click → instant feedback). The cost of
  // the always-on path is two pointer writes per channel-switch click.
  pendingClickHint = { source, evTimeStamp };
}

/**
 * Non-destructive read of the pending click source. Used by
 * `openChannelWithContext` to decide whether to wrap the channel-
 * switch setState bag in `startTransition`. `csMark("click")` is what
 * actually consumes (clears) the hint, after the setState bag has
 * been dispatched — so this peek + a later consume is safe.
 */
export function csPeekClickSource(): ClickSource | null {
  return pendingClickHint?.source ?? null;
}

const now = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

// ─── Render-time probes ───────────────────────────────────────────
// `csNow()` returns a timestamp (or 0 when the profile is off — keeps
// the on/off cost to a single boolean test at the callsite). `csLog`
// emits `[r] <label> dur=Xms` only when the elapsed time exceeds the
// noise floor — anything <3ms is too small to be a longtask source.
//
// Use them in pairs to time:
//   • component body (sync function body): t0 at top, csLog before return
//   • component subtree (includes child commits): t0 at top of body,
//     csLog inside useLayoutEffect → measures everything React did
//     between body start and commit
//
// Interleaves chronologically with `[cs]` switch lines and `[lt]`
// longtask lines so you can attribute longtasks to specific component
// renders.
export function csNow(): number {
  return CHANNEL_SWITCH_PROFILE ? now() : 0;
}

export function csLog(label: string, t0: number, extra?: string): void {
  if (!CHANNEL_SWITCH_PROFILE) return;
  const dur = now() - t0;
  // Noise floor — anything under 3ms is unlikely to be a longtask
  // contributor and would just drown out the signal.
  if (dur < 3) return;
  console.log(`[r] ${label} ${dur.toFixed(1)}ms${extra ? ` ${extra}` : ""}`);
}

// ─── Per-component render counters ─────────────────────────────────
// Reset on every `csMark("click")`; flushed inside `emitAndClear`
// alongside the `[cs]` line. Tells you how many instances of a given
// component (e.g. ChannelItem, DMRowItem, MessageRow) actually
// re-rendered during the switch.
//
// Why this is useful: with `React.memo`, only items whose props
// changed should re-render — for a selection flip that's typically
// 2 (old selected + new selected). If you see ChannelItem=22 it
// means the memo is busted by an unstable prop (probably a fresh
// callback or object reference each parent render).
let switchRenderCounts: Map<string, number> | null = null;

export function csCountRender(label: string): void {
  if (!CHANNEL_SWITCH_PROFILE) return;
  if (!switchRenderCounts) return;
  switchRenderCounts.set(label, (switchRenderCounts.get(label) ?? 0) + 1);
}

function emitAndClear(channelId: string): void {
  const e = entries.get(channelId);
  if (!e || e.settled === undefined) return;
  const parts: string[] = [];
  if (e.emit !== undefined) {
    parts.push(`click→emit=${(e.emit - e.click).toFixed(0)}ms`);
  }
  if (e.paint !== undefined && e.emit !== undefined) {
    parts.push(`emit→paint=${(e.paint - e.emit).toFixed(0)}ms`);
  } else if (e.paint !== undefined) {
    parts.push(`click→paint=${(e.paint - e.click).toFixed(0)}ms`);
  }
  if (e.paint !== undefined) {
    parts.push(`paint→settled=${(e.settled - e.paint).toFixed(0)}ms`);
  } else {
    parts.push(`click→settled=${(e.settled - e.click).toFixed(0)}ms`);
  }
  parts.push(`total=${(e.settled - e.click).toFixed(0)}ms`);
  if (e.msgs !== undefined) parts.push(`msgs=${e.msgs}`);
  parts.push(`q=${e.qAtClick}`);
  if (e.sinceLastClick >= 0) {
    parts.push(`since=${e.sinceLastClick.toFixed(0)}ms`);
  }
  if (e.evDelayMs >= 0) {
    parts.push(`evDelay=${e.evDelayMs.toFixed(0)}ms`);
  }
  parts.push(`src=${e.source}`);
  // Anchor timestamp for cross-referencing with [lt] long-task lines.
  // The physical keydown was at `click - evDelayMs`, so a long task
  // whose `end` is close to (click - evDelayMs) is the suspect that
  // blocked the keystroke from being dispatched.
  parts.push(`click=${e.click.toFixed(0)}`);

  console.log(`[cs] ${channelId.slice(-6)} ${parts.join(" ")}`);

  // Flush the per-component render counters tracked since the most
  // recent click. Logged as a sibling `[rc]` line so it's grep-able
  // and pairs with the `[cs]` it follows.
  if (switchRenderCounts && switchRenderCounts.size > 0) {
    const rcParts: string[] = [];
    for (const [label, count] of switchRenderCounts) {
      rcParts.push(`${label}=${count}`);
    }
    console.log(`[rc] ${channelId.slice(-6)} ${rcParts.join(" ")}`);
  }
  switchRenderCounts = null;

  entries.delete(channelId);
}

export function csMark(
  stage: Stage,
  channelId: string | null | undefined,
  extra?: { msgs?: number },
): void {
  if (!CHANNEL_SWITCH_PROFILE) return;
  if (!channelId) return;
  const t = now();

  if (stage === "click") {
    const hint = pendingClickHint;
    pendingClickHint = null;
    const sinceLastClick = lastClickAt >= 0 ? t - lastClickAt : -1;
    const evDelayMs = hint ? t - hint.evTimeStamp : -1;
    const source: ClickSource = hint?.source ?? "other";
    // Drop any previous unsettled entry for the same channel so its
    // stale in-flight slot doesn't double-count.
    const existing = entries.get(channelId);
    if (existing && existing.settled === undefined) {
      inFlightCount = Math.max(0, inFlightCount - 1);
    }
    // `qAtClick` measures OTHER switches in flight — exclude this one,
    // which we're about to add.
    entries.set(channelId, {
      click: t,
      qAtClick: inFlightCount,
      sinceLastClick,
      evDelayMs,
      source,
    });
    inFlightCount += 1;
    lastClickAt = t;
    // Start a fresh render-count bucket for this switch. Counts that
    // accumulated since the last click belong to a switch we already
    // flushed (or to a stale switch that never settled) — drop them.
    switchRenderCounts = new Map();
    return;
  }

  const e = entries.get(channelId);
  if (!e) return;

  if (stage === "emit") {
    if (e.emit === undefined) {
      e.emit = t;
      if (extra?.msgs !== undefined) e.msgs = extra.msgs;
    }
    return;
  }

  if (stage === "paint") {
    if (e.paint === undefined) e.paint = t;
    return;
  }

  // stage === "settled" — record once, then defer emission through a
  // double rAF so any post-commit paint marks scheduled in the same
  // frame can land on this entry first.
  if (e.settled !== undefined) return;
  e.settled = t;
  inFlightCount = Math.max(0, inFlightCount - 1);
  if (typeof requestAnimationFrame !== "function") {
    emitAndClear(channelId);
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => emitAndClear(channelId));
  });
}
